import { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter, DialogDescription } from '@/components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Input } from '@/components/ui/input';
import {
  Upload,
  FileSpreadsheet,
  FolderSync,
  CheckCircle2,
  AlertCircle,
  Loader2,
  MapPin,
  Tractor,
  Users,
  RefreshCw,
  Trash2,
  Download,
  ArrowRight,
  FileArchive,
  Leaf,
  Calendar,
  BarChart3,
  Info,
  ChevronRight,
} from 'lucide-react';
import { toast } from 'sonner';
import api from '@/lib/api';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function GeofoliaImportPage() {
  const [isUploading, setIsUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [lastImport, setLastImport] = useState(null);
  const [parcelles, setParcelles] = useState([]);
  const [interventions, setInterventions] = useState([]);
  const [exploitations, setExploitations] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('import');
  const [searchTerm, setSearchTerm] = useState('');
  const [showResetDialog, setShowResetDialog] = useState(false);
  const [isSyncing, setIsSyncing] = useState(false);

  // Charger les données existantes
  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const [parcellesRes, interventionsRes, exploitationsRes] = await Promise.all([
        api.get('/geofolia/parcelles'),
        api.get('/geofolia/interventions'),
        api.get('/geofolia/exploitations'),
      ]);
      
      setParcelles(parcellesRes.data.parcelles || []);
      setStats(parcellesRes.data.stats || null);
      setInterventions(interventionsRes.data.interventions || []);
      setExploitations(exploitationsRes.data.exploitations || []);
    } catch (error) {
      console.error('Erreur chargement données Geofolia:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadData();
  }, [loadData]);

  // Upload du fichier ZIP
  const handleFileUpload = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    // Vérifier l'extension
    const validExtensions = ['.zip', '.xlsx', '.xls'];
    const fileExt = file.name.toLowerCase().substring(file.name.lastIndexOf('.'));
    if (!validExtensions.includes(fileExt)) {
      toast.error('Format non supporté. Utilisez un fichier ZIP ou Excel (.xlsx)');
      return;
    }

    setIsUploading(true);
    setUploadProgress(10);

    const formData = new FormData();
    formData.append('file', file);

    try {
      setUploadProgress(30);
      
      const response = await api.post('/geofolia/import-zip', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
        timeout: 300000, // 5 min - import peut prendre 25-60s côté serveur
        onUploadProgress: (progressEvent) => {
          const percentCompleted = Math.round((progressEvent.loaded * 50) / progressEvent.total) + 30;
          setUploadProgress(percentCompleted);
        },
      });

      setUploadProgress(90);
      
      if (response.data.success) {
        setLastImport(response.data);
        toast.success(`Import réussi ! ${response.data.parcelles_importees} parcelles, ${response.data.interventions_importees} interventions`);
        
        // Recharger les données
        await loadData();
        setActiveTab('parcelles');
      } else {
        toast.error('Erreur lors de l\'import');
      }
      
      setUploadProgress(100);
    } catch (error) {
      console.error('Erreur upload:', error);
      // Message d'erreur détaillé pour aider au diagnostic
      let msg;
      if (error.code === 'ECONNABORTED') {
        msg = 'Upload trop long (timeout). Fichier > 20 Mo ? Relancez ou contactez support.';
      } else if (error.response?.status === 413) {
        msg = 'Fichier trop volumineux pour le serveur. Max 25 Mo.';
      } else if (error.response?.data?.detail) {
        msg = `Erreur serveur : ${error.response.data.detail}`;
      } else if (error.response?.status) {
        msg = `Erreur HTTP ${error.response.status}`;
      } else {
        msg = `Erreur réseau : ${error.message}`;
      }
      toast.error(msg, { duration: 8000 });
    } finally {
      setIsUploading(false);
      setUploadProgress(0);
      // Reset input
      event.target.value = '';
    }
  };

  // Synchroniser vers le CRM
  const handleSyncToCRM = async () => {
    setIsSyncing(true);
    try {
      const response = await api.post('/geofolia/sync-to-crm');
      if (response.data.success) {
        toast.success(`Synchronisation réussie ! ${response.data.clients_crees} clients créés, ${response.data.clients_mis_a_jour} mis à jour`);
      }
    } catch (error) {
      console.error('Erreur sync CRM:', error);
      toast.error('Erreur lors de la synchronisation');
    } finally {
      setIsSyncing(false);
    }
  };

  // Réinitialiser les données
  const handleReset = async () => {
    try {
      const response = await api.delete('/geofolia/reset');
      toast.success(`Données supprimées : ${response.data.parcelles_supprimees} parcelles, ${response.data.interventions_supprimees} interventions`);
      setShowResetDialog(false);
      await loadData();
    } catch (error) {
      console.error('Erreur reset:', error);
      toast.error('Erreur lors de la suppression');
    }
  };

  // Filtrer les parcelles
  const filteredParcelles = parcelles.filter(p => 
    !searchTerm || 
    p.nom?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    p.culture?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    p.exploitation_nom?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  // Filtrer les interventions
  const filteredInterventions = interventions.filter(i =>
    !searchTerm ||
    i.operation?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    i.exploitation_nom?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 p-4 md:p-6" data-testid="geofolia-import-page">
      {/* Header */}
      <div className="mb-6">
        <div className="flex items-center gap-3 mb-2">
          <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-emerald-500 to-green-600 flex items-center justify-center">
            <FolderSync className="w-6 h-6 text-white" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-slate-800">Synchronisation Geofolia</h1>
            <p className="text-slate-500">Importez vos parcelles et interventions depuis Geofolia</p>
          </div>
        </div>
      </div>

      {/* Stats rapides */}
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
          <Card className="bg-white/80 backdrop-blur">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-emerald-100 flex items-center justify-center">
                  <MapPin className="w-5 h-5 text-emerald-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-slate-800">{stats.total}</p>
                  <p className="text-xs text-slate-500">Parcelles</p>
                </div>
              </div>
            </CardContent>
          </Card>
          
          <Card className="bg-white/80 backdrop-blur">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-blue-100 flex items-center justify-center">
                  <BarChart3 className="w-5 h-5 text-blue-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-slate-800">{stats.surface_totale?.toFixed(1)}</p>
                  <p className="text-xs text-slate-500">Hectares</p>
                </div>
              </div>
            </CardContent>
          </Card>
          
          <Card className="bg-white/80 backdrop-blur">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-amber-100 flex items-center justify-center">
                  <Users className="w-5 h-5 text-amber-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-slate-800">{stats.nb_exploitations || exploitations.length}</p>
                  <p className="text-xs text-slate-500">Exploitations</p>
                </div>
              </div>
            </CardContent>
          </Card>
          
          <Card className="bg-white/80 backdrop-blur">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-purple-100 flex items-center justify-center">
                  <Tractor className="w-5 h-5 text-purple-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-slate-800">{interventions.length}</p>
                  <p className="text-xs text-slate-500">Interventions</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-4">
        <TabsList className="bg-white/80 backdrop-blur p-1">
          <TabsTrigger value="import" className="data-[state=active]:bg-emerald-500 data-[state=active]:text-white">
            <Upload className="w-4 h-4 mr-2" />
            Import
          </TabsTrigger>
          <TabsTrigger value="parcelles" className="data-[state=active]:bg-emerald-500 data-[state=active]:text-white">
            <MapPin className="w-4 h-4 mr-2" />
            Parcelles ({parcelles.length})
          </TabsTrigger>
          <TabsTrigger value="interventions" className="data-[state=active]:bg-emerald-500 data-[state=active]:text-white">
            <Tractor className="w-4 h-4 mr-2" />
            Interventions ({interventions.length})
          </TabsTrigger>
          <TabsTrigger value="exploitations" className="data-[state=active]:bg-emerald-500 data-[state=active]:text-white">
            <Users className="w-4 h-4 mr-2" />
            Exploitations ({exploitations.length})
          </TabsTrigger>
        </TabsList>

        {/* Tab Import */}
        <TabsContent value="import" className="space-y-4">
          <div className="grid md:grid-cols-2 gap-6">
            {/* Zone d'upload */}
            <Card className="bg-white shadow-lg">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <FileArchive className="w-5 h-5 text-emerald-600" />
                  Importer un fichier
                </CardTitle>
                <CardDescription>
                  Exportez vos données depuis Geofolia au format ZIP ou Excel
                </CardDescription>
              </CardHeader>
              <CardContent>
                <label 
                  className={`relative flex flex-col items-center justify-center w-full h-48 border-2 border-dashed rounded-xl cursor-pointer transition-all ${
                    isUploading 
                      ? 'border-emerald-400 bg-emerald-50' 
                      : 'border-slate-300 hover:border-emerald-400 hover:bg-emerald-50/50'
                  }`}
                  data-testid="upload-zone"
                >
                  <input
                    type="file"
                    accept=".zip,.xlsx,.xls"
                    onChange={handleFileUpload}
                    className="hidden"
                    disabled={isUploading}
                    data-testid="file-input"
                  />
                  
                  {isUploading ? (
                    <div className="text-center">
                      <Loader2 className="w-12 h-12 text-emerald-500 animate-spin mx-auto mb-3" />
                      <p className="text-sm font-medium text-slate-700">Import en cours...</p>
                      <Progress value={uploadProgress} className="w-48 mt-3" />
                    </div>
                  ) : (
                    <div className="text-center">
                      <Upload className="w-12 h-12 text-slate-400 mx-auto mb-3" />
                      <p className="text-sm font-medium text-slate-700">Glissez votre fichier ici</p>
                      <p className="text-xs text-slate-500 mt-1">ou cliquez pour parcourir</p>
                      <div className="flex gap-2 mt-3 justify-center">
                        <Badge variant="outline">.zip</Badge>
                        <Badge variant="outline">.xlsx</Badge>
                        <Badge variant="outline">.xls</Badge>
                      </div>
                    </div>
                  )}
                </label>

                {/* Résultat du dernier import */}
                {lastImport && (
                  <Alert className="mt-4 bg-emerald-50 border-emerald-200">
                    <CheckCircle2 className="w-4 h-4 text-emerald-600" />
                    <AlertTitle className="text-emerald-800">Import réussi</AlertTitle>
                    <AlertDescription className="text-emerald-700">
                      <ul className="mt-2 space-y-1 text-sm">
                        <li>• {lastImport.parcelles_importees} parcelles importées</li>
                        <li>• {lastImport.interventions_importees} interventions importées</li>
                        <li>• {lastImport.exploitations_importees} exploitations importées</li>
                        {lastImport.materiels_importes > 0 && (
                          <li>• {lastImport.materiels_importes} matériels importés</li>
                        )}
                      </ul>
                    </AlertDescription>
                  </Alert>
                )}
              </CardContent>
            </Card>

            {/* Guide d'export Geofolia */}
            <Card className="bg-white shadow-lg">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Info className="w-5 h-5 text-blue-600" />
                  Comment exporter depuis Geofolia
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-3">
                  <div className="flex gap-3">
                    <div className="w-6 h-6 rounded-full bg-emerald-100 flex items-center justify-center flex-shrink-0">
                      <span className="text-xs font-bold text-emerald-700">1</span>
                    </div>
                    <div>
                      <p className="font-medium text-slate-800">Parcelles et assolement</p>
                      <p className="text-sm text-slate-500">
                        Accueil → Export → Exploitations, parcelles et interventions (xls)
                      </p>
                    </div>
                  </div>
                  
                  <div className="flex gap-3">
                    <div className="w-6 h-6 rounded-full bg-emerald-100 flex items-center justify-center flex-shrink-0">
                      <span className="text-xs font-bold text-emerald-700">2</span>
                    </div>
                    <div>
                      <p className="font-medium text-slate-800">Interventions</p>
                      <p className="text-sm text-slate-500">
                        Parcellaire → Interventions → Exporter
                      </p>
                    </div>
                  </div>
                  
                  <div className="flex gap-3">
                    <div className="w-6 h-6 rounded-full bg-emerald-100 flex items-center justify-center flex-shrink-0">
                      <span className="text-xs font-bold text-emerald-700">3</span>
                    </div>
                    <div>
                      <p className="font-medium text-slate-800">Créez un ZIP</p>
                      <p className="text-sm text-slate-500">
                        Regroupez tous les fichiers Excel dans un ZIP et importez-le ici
                      </p>
                    </div>
                  </div>
                </div>

                <Alert className="bg-blue-50 border-blue-200">
                  <Info className="w-4 h-4 text-blue-600" />
                  <AlertDescription className="text-blue-700 text-sm">
                    Chaque import <strong>synchronise</strong> vos données. Les parcelles existantes sont mises à jour, les nouvelles sont ajoutées.
                  </AlertDescription>
                </Alert>
              </CardContent>
            </Card>
          </div>

          {/* Actions supplémentaires */}
          <Card className="bg-white shadow-lg">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <FolderSync className="w-5 h-5 text-purple-600" />
                Actions de synchronisation
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex flex-wrap gap-3">
                <Button 
                  onClick={handleSyncToCRM}
                  disabled={isSyncing || exploitations.length === 0}
                  className="bg-purple-600 hover:bg-purple-700"
                  data-testid="sync-crm-btn"
                >
                  {isSyncing ? (
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  ) : (
                    <ArrowRight className="w-4 h-4 mr-2" />
                  )}
                  Synchroniser vers CRM
                </Button>
                
                <Button 
                  variant="outline"
                  onClick={loadData}
                  disabled={loading}
                  data-testid="refresh-btn"
                >
                  <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
                  Actualiser
                </Button>
                
                <Dialog open={showResetDialog} onOpenChange={setShowResetDialog}>
                  <DialogTrigger asChild>
                    <Button 
                      variant="outline" 
                      className="text-red-600 border-red-200 hover:bg-red-50"
                      disabled={parcelles.length === 0 && interventions.length === 0}
                      data-testid="reset-btn"
                    >
                      <Trash2 className="w-4 h-4 mr-2" />
                      Réinitialiser
                    </Button>
                  </DialogTrigger>
                  <DialogContent>
                    <DialogHeader>
                      <DialogTitle>Confirmer la suppression</DialogTitle>
                      <DialogDescription>
                        Cette action supprimera toutes les données importées depuis Geofolia. Cette action est irréversible.
                      </DialogDescription>
                    </DialogHeader>
                    <DialogFooter>
                      <Button variant="outline" onClick={() => setShowResetDialog(false)}>
                        Annuler
                      </Button>
                      <Button variant="destructive" onClick={handleReset}>
                        Supprimer tout
                      </Button>
                    </DialogFooter>
                  </DialogContent>
                </Dialog>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Tab Parcelles */}
        <TabsContent value="parcelles">
          <Card className="bg-white shadow-lg">
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle className="flex items-center gap-2">
                  <MapPin className="w-5 h-5 text-emerald-600" />
                  Parcelles importées
                </CardTitle>
                <Input
                  placeholder="Rechercher..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-64"
                  data-testid="search-parcelles"
                />
              </div>
            </CardHeader>
            <CardContent>
              {loading ? (
                <div className="flex items-center justify-center py-12">
                  <Loader2 className="w-8 h-8 animate-spin text-emerald-500" />
                </div>
              ) : filteredParcelles.length === 0 ? (
                <div className="text-center py-12 text-slate-500">
                  <MapPin className="w-12 h-12 mx-auto mb-3 opacity-30" />
                  <p>Aucune parcelle importée</p>
                  <p className="text-sm">Importez un fichier Geofolia pour commencer</p>
                </div>
              ) : (
                <ScrollArea className="h-[500px]">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Nom</TableHead>
                        <TableHead>Exploitation</TableHead>
                        <TableHead>Culture</TableHead>
                        <TableHead>Surface (ha)</TableHead>
                        <TableHead>Îlot</TableHead>
                        <TableHead>Campagne</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {filteredParcelles.map((parcelle, idx) => (
                        <TableRow key={parcelle.id || idx}>
                          <TableCell className="font-medium">{parcelle.nom}</TableCell>
                          <TableCell>
                            <Badge variant="outline">{parcelle.exploitation_nom || '-'}</Badge>
                          </TableCell>
                          <TableCell>
                            {parcelle.culture ? (
                              <div className="flex items-center gap-2">
                                <Leaf className="w-4 h-4 text-green-500" />
                                {parcelle.culture}
                              </div>
                            ) : '-'}
                          </TableCell>
                          <TableCell>{parcelle.surface?.toFixed(2) || '-'}</TableCell>
                          <TableCell>{parcelle.ilot || '-'}</TableCell>
                          <TableCell>
                            <Badge variant="secondary">{parcelle.campagne || '2025'}</Badge>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </ScrollArea>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Tab Interventions */}
        <TabsContent value="interventions">
          <Card className="bg-white shadow-lg">
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle className="flex items-center gap-2">
                  <Tractor className="w-5 h-5 text-purple-600" />
                  Interventions importées
                </CardTitle>
                <Input
                  placeholder="Rechercher..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-64"
                  data-testid="search-interventions"
                />
              </div>
            </CardHeader>
            <CardContent>
              {loading ? (
                <div className="flex items-center justify-center py-12">
                  <Loader2 className="w-8 h-8 animate-spin text-purple-500" />
                </div>
              ) : filteredInterventions.length === 0 ? (
                <div className="text-center py-12 text-slate-500">
                  <Tractor className="w-12 h-12 mx-auto mb-3 opacity-30" />
                  <p>Aucune intervention importée</p>
                  <p className="text-sm">Importez un fichier Geofolia pour commencer</p>
                </div>
              ) : (
                <ScrollArea className="h-[500px]">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Date</TableHead>
                        <TableHead>Opération</TableHead>
                        <TableHead>Exploitation</TableHead>
                        <TableHead>Culture</TableHead>
                        <TableHead>Surface (ha)</TableHead>
                        <TableHead>Durée</TableHead>
                        <TableHead>Statut</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {filteredInterventions.map((interv, idx) => (
                        <TableRow key={interv.id || idx}>
                          <TableCell>
                            <div className="flex items-center gap-2">
                              <Calendar className="w-4 h-4 text-slate-400" />
                              {interv.date_debut || '-'}
                            </div>
                          </TableCell>
                          <TableCell className="font-medium">{interv.operation || '-'}</TableCell>
                          <TableCell>
                            <Badge variant="outline">{interv.exploitation_nom || '-'}</Badge>
                          </TableCell>
                          <TableCell>{interv.culture || '-'}</TableCell>
                          <TableCell>{interv.surface_travaillee?.toFixed(2) || '-'}</TableCell>
                          <TableCell>{interv.duree || '-'}</TableCell>
                          <TableCell>
                            <Badge className={
                              interv.statut === 'Réalisée' 
                                ? 'bg-green-100 text-green-700' 
                                : 'bg-amber-100 text-amber-700'
                            }>
                              {interv.statut || 'Réalisée'}
                            </Badge>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </ScrollArea>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Tab Exploitations */}
        <TabsContent value="exploitations">
          <Card className="bg-white shadow-lg">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Users className="w-5 h-5 text-amber-600" />
                Exploitations importées
              </CardTitle>
            </CardHeader>
            <CardContent>
              {loading ? (
                <div className="flex items-center justify-center py-12">
                  <Loader2 className="w-8 h-8 animate-spin text-amber-500" />
                </div>
              ) : exploitations.length === 0 ? (
                <div className="text-center py-12 text-slate-500">
                  <Users className="w-12 h-12 mx-auto mb-3 opacity-30" />
                  <p>Aucune exploitation importée</p>
                  <p className="text-sm">Importez un fichier Geofolia pour commencer</p>
                </div>
              ) : (
                <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {exploitations.map((expl, idx) => (
                    <Card key={expl.id || idx} className="bg-slate-50">
                      <CardContent className="p-4">
                        <div className="flex items-start justify-between mb-3">
                          <div className="w-10 h-10 rounded-lg bg-amber-100 flex items-center justify-center">
                            <Users className="w-5 h-5 text-amber-600" />
                          </div>
                          <Badge variant="outline" className="bg-white">
                            {expl.certification || 'Standard'}
                          </Badge>
                        </div>
                        <h3 className="font-semibold text-slate-800 mb-1">{expl.raison_sociale}</h3>
                        <p className="text-sm text-slate-500 mb-3">{expl.commune || 'Non renseignée'}</p>
                        <div className="flex gap-4 text-sm">
                          <div>
                            <p className="text-slate-500">Parcelles</p>
                            <p className="font-semibold">{expl.nb_parcelles || 0}</p>
                          </div>
                          <div>
                            <p className="text-slate-500">Surface</p>
                            <p className="font-semibold">{expl.surface_totale?.toFixed(1) || 0} ha</p>
                          </div>
                        </div>
                        {expl.siret && (
                          <p className="text-xs text-slate-400 mt-2">SIRET: {expl.siret}</p>
                        )}
                      </CardContent>
                    </Card>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}
